import React, { useContext } from "react";

import {
  ApplicationInsights,
  ICustomProperties,
  IPageViewTelemetry,
  ITraceTelemetry,
} from "@microsoft/applicationinsights-web";
import { useAuth0 } from "@auth0/auth0-react";
import { OrganisationContext } from "../context/OrganisationContext";
import { OUTLOOK_ADDIN_CUSTOM_EVENT_PREFIX, withCustomEventPrefix } from "../telemetry/customEventPrefix";

export { SeverityLevel } from "@microsoft/applicationinsights-web";

interface AppInsightsContextValues {
  appInsights: ApplicationInsights;
}

export const AppInsightsContext = React.createContext<AppInsightsContextValues>({} as AppInsightsContextValues);

export const AppInsightsContextProvider = ({
  instrumentationKey,
  children,
}: React.PropsWithChildren<{ instrumentationKey: string }>) => {
  const appInsights = React.useMemo(() => {
    const appInsights = new ApplicationInsights({
      config: {
        disableFetchTracking: false,
        instrumentationKey: instrumentationKey,
      },
    });

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer((envelope) => {
      if (envelope.tags) {
        envelope.tags["ai.cloud.role"] = "attelas-outlook-plugin";
        envelope.tags["ai.cloud.roleInstance"] = "attelas-outlook-plugin";
        // envelope.tags["ai.user.authUserId"] = user?.sub;
        // envelope.tags["ai.user.organisationId"] = organisation?.id;
        // envelope.tags["ai.user.organisation"] = organisation?.name;
      }
    });

    return appInsights;
  }, [instrumentationKey]);

  return <AppInsightsContext.Provider value={{ appInsights: appInsights }}>{children}</AppInsightsContext.Provider>;
};

function sanitizeData(data: Record<string, unknown>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(data)
      .filter(([, value]) => value != null)
      .map(([key, value]) => [key, typeof value === "string" ? value : JSON.stringify(value)])
  );
}

export const useAppInsights = () => {
  const value = useContext(AppInsightsContext);
  const { user } = useAuth0();
  const { organisation } = useContext(OrganisationContext);

  const commonData = React.useMemo(() => {
    return sanitizeData({
      userId: user?.sub,
      userEmail: user?.email,
      organisationId: organisation?.id,
      organisation: organisation?.name,
    });
  }, [organisation, user]);

  type AppInsightEvent = {
    name: string;
    properties?: Record<string, string>;
  };

  const trackEvent = React.useCallback(
    ({ name, properties }: AppInsightEvent, customProperties?: ICustomProperties) => {
      value.appInsights.trackEvent(
        {
          name: withCustomEventPrefix(name, OUTLOOK_ADDIN_CUSTOM_EVENT_PREFIX),
          properties: {
            ...commonData,
            ...properties,
          },
        },
        customProperties
      );
    },
    [commonData]
  );
  const trackTrace = React.useCallback(
    ({ properties, ...rest }: ITraceTelemetry, customProperties?: ICustomProperties) => {
      value.appInsights.trackTrace(
        {
          properties: {
            ...commonData,
            ...properties,
          },
          ...rest,
        },
        customProperties
      );
    },
    [commonData]
  );
  const trackPageView = React.useCallback(
    ({ properties, ...rest }: IPageViewTelemetry) => {
      value.appInsights.trackPageView({
        properties: {
          ...commonData,
          ...properties,
        },
        ...rest,
      });
    },
    [commonData]
  );

  return {
    trackEvent,
    trackTrace,
    trackPageView,
    userId: value.appInsights.context.user.id,
  };
};
